import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import {
  TableCellDotsWithPopup,
  TableCellMultiline,
  TableCellThreeDotsButton,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import { PharmaStpFileTableDataModel } from "../../../data-model/tablesData";
import { CallingStatus } from "../../../enums/callingStatus";
import { CmmStpStatus } from "../../../enums/cmmStpStatus";
import {
  SecondStpStatus,
  SecondStpStatusLabels,
} from "../../../enums/secondStpStatus";
import { PharmaStpFileTableColumnKeys } from "../../../enums/tableColumnKeys";
import { mapPharmaStpFileExtendedRowFields } from "./mapPharmaStpFileExtendedRowFields";
import {
  extractBatchFromFileName,
  extractFormattedDateFromFileName,
} from "./utils/extractDateFromFileName";
import { formatDob } from "./utils/formatDob";
import { getCmmStatuS } from "./utils/getCorrectCmmStatus";
import { getDenialLetterUploadStatus } from "./utils/getCorrectDenialLetterUploadStatus";
import { getStpFileStatus } from "./utils/getCorrectStatus";
import { getTextNotesStatus } from "./utils/getCorrectTextNotesStatus";
import getPoc from "./utils/getPoc";
import { getRouteViaLabel } from "./utils/getRouteViaLabel";
import { getSftpBadgeStyles } from "./utils/getSftpBadgeStyles";
import {
  getBgColorForStpStatus,
  getTextColorForStpStatus,
} from "./utils/getStpStatusColors";
import { getSendToAppealBadgeButtonValue } from "../../pharmaOncologySubmission/table/getSendToAppealBadgeButtonValue";

/** Checkbox ids for the STP workflow `DotsWithPopup` column (aligned with `PHARMA_STP_DETAILS_POPUP_BQ_FIELDS`). */
const PHARMA_STP_DETAILS_POPUP_CHECKBOX_ITEMS: Array<{
  id: string;
  label: string;
}> = [
  { id: "stp_details_cmm", label: "CMM" },
  { id: "stp_details_text_note", label: "Text Note" },
  { id: "stp_details_letter_upload", label: "Letter Upload" },
];

/**
 * Maps each STP checklist checkbox id to the BigQuery `update_data` key (supports `meta.*` JSON paths).
 * Keys must match `PHARMA_STP_DETAILS_POPUP_CHECKBOX_ITEMS` `id` values.
 */
export const PHARMA_STP_DETAILS_POPUP_BQ_FIELDS: Record<string, string> = {
  stp_details_cmm: "meta.cmm_st_rpa_status",
  stp_details_text_note: "text_note_status",
  stp_details_letter_upload: "meta.denial_letter_upload_status",
};

export const PharmaStpFileTableHeader: TableHeader[] = [
  {
    label: "Batch",
    key: PharmaStpFileTableColumnKeys.BATCH,
    order: 1,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "DOW",
    key: PharmaStpFileTableColumnKeys.DATE_OF_WORK,
    order: 2,
    width: 7,
    sortable: true,
    type: TableCellType.STRING,
  },
  // {
  //   label: "MRN",
  //   key: PharmaStpFileTableColumnKeys.PATIENT_MRN,
  //   order: 3,
  //   width: 8,
  //   sortable: true,
  //   type: TableCellType.STRING,
  // },
  {
    label: "Patient Name",
    key: PharmaStpFileTableColumnKeys.PATIENT_NAME,
    order: 3,
    width: 12,
    sortable: false,
    type: TableCellType.MULTILINE,
  },
  {
    label: "DOB",
    key: PharmaStpFileTableColumnKeys.DOB,
    order: 4,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Drug",
    key: PharmaStpFileTableColumnKeys.DRUG,
    order: 5,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  // {
  //   label: "BIN",
  //   key: PharmaStpFileTableColumnKeys.BIN,
  //   order: 7,
  //   width: 8,
  //   sortable: false,
  //   type: TableCellType.STRING,
  // },
  {
    label: "CoverMyMeds ID",
    key: PharmaStpFileTableColumnKeys.CMM_ID,
    order: 6,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },

  {
    label: "2nd STP Status",
    key: PharmaStpFileTableColumnKeys.SECOND_STP_STATUS,
    order: 7,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Response Status",
    key: PharmaStpFileTableColumnKeys.RESPONSE_STATUS,
    order: 8,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Route Via",
    key: PharmaStpFileTableColumnKeys.ROUTE_VIA,
    order: 9,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE,
  },

  {
    label: "Add",
    key: PharmaStpFileTableColumnKeys.ADD,
    order: 10,
    width: 5,
    sortable: false,
    type: TableCellType.ADD_KEY_BUTTON,
  },
  {
    label: "POC",
    key: PharmaStpFileTableColumnKeys.POC,
    order: 11,
    width: 6,
    sortable: false,
    type: TableCellType.STRING,
  },

  {
    label: "CMM",
    key: PharmaStpFileTableColumnKeys.CMM,
    order: 12,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "Text Note",
    key: PharmaStpFileTableColumnKeys.TEXT_NOTE,
    order: 13,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "Letter Upload",
    key: PharmaStpFileTableColumnKeys.LETTER_UPLOAD,
    order: 14,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "SFTP",
    key: PharmaStpFileTableColumnKeys.SFTP,
    order: 15,
    width: 6,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "Send to Appeal",
    key: PharmaStpFileTableColumnKeys.SEND_TO_APPEAL,
    order: 16,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "",
    key: PharmaStpFileTableColumnKeys.INSURANCE_INFO_ICON,
    order: 17,
    width: 4,
    sortable: false,
    type: TableCellType.INSURANCE_INFO_ICON,
  },
  {
    label: "",
    key: PharmaStpFileTableColumnKeys.DETAILS_POPUP,
    order: 18,
    width: 6,
    sortable: false,
    type: TableCellType.DOTS_WITH_POPUP,
  },
  {
    label: "",
    key: PharmaStpFileTableColumnKeys.IDENTIFIER,
    order: 0,
    width: 3,
    sortable: false,
    type: TableCellType.COPY_ICON,
  },
];

/**
 * Optional callbacks when generating pharma STP file table rows (e.g. refetch after BigQuery update).
 */
export interface PharmaStpFileTableDataCallbacks {
  /** Fired after the workflow checklist popup successfully updates BigQuery. */
  onStpDetailsPopupMarkSuccess?: () => void;
}

export const generateTableDataForPharmaStpFileTable = (
  data: PharmaStpFileModel[],
  callbacks?: PharmaStpFileTableDataCallbacks,
): PharmaStpFileTableDataModel[] => {
  return data.map((item) => {
    const fileName: string = item?.filename ?? "";
    const batch: string = extractBatchFromFileName(fileName);
    const dow: string = extractFormattedDateFromFileName(fileName);
    const patient_mrn: string = item?.patient_mrn ?? "";
    const patientName: TableCellMultiline = {
      mainText: item?.patient_name ?? "",
      secondaryText: `MRN : ${item?.patient_mrn ?? ""}`,
    };
    const dob: string = formatDob(item?.dob);
    const drug: string = item?.drug ?? "";

    const cmmIdValue = item?.covermymed_id;
    const cmmId: string =
      !cmmIdValue || cmmIdValue === "nan" || cmmIdValue === "NaN"
        ? ""
        : String(cmmIdValue);

    const poc: string = getPoc(item?.poc ?? "");
    const identifier: string = item?.identifier ?? "";

    // const shouldDisableAddButton = Boolean(
    //   item?.covermymed_id &&
    //   item.covermymed_id !== "nan" &&
    //   item.covermymed_id !== "NaN" &&
    //   item.covermymed_id !== "",
    // );

    const addText = item?.covermymed_id ? "Edit" : "Add";

    const add = {
      value: "Add",
      isDisabled: false,
      title: addText,
    };

    const sftpStyles = getSftpBadgeStyles(item?.sftp_status);
    const sftp: TableCellWithBadge = {
      text: sftpStyles.title,
      color: sftpStyles.textColor,
      bgColor: sftpStyles.bgColor,
      hoverText: sftpStyles.title,
    };

    const secondStpStatusBadge: TableCellWithBadge = item?.second_stp_status
      ? {
          text: SecondStpStatusLabels[
            item.second_stp_status as SecondStpStatus
          ],
          color: getTextColorForStpStatus(
            item.second_stp_status as CmmStpStatus,
          ),
          bgColor: getBgColorForStpStatus(
            item.second_stp_status as CmmStpStatus,
          ),
        }
      : {
          text: "Not Sent",
          color: "#111111",
          bgColor: "#F5F5F5",
        };

    const responseStpStatusBadge: TableCellWithBadge = item?.response_status
      ? getStpFileStatus(item.response_status)
      : {
          text: item?.response_status || "--",
          color: "#0F0F0F",
          bgColor: "#F5F5F5",
        };

    const routeViaLabel = getRouteViaLabel(item?.route_via);
    const routeViaBadge: TableCellWithBadge = routeViaLabel
      ? { text: routeViaLabel, color: "#0F0F0F", bgColor: "#F5F5F5" }
      : { text: "--", color: "#0F0F0F", bgColor: "#F5F5F5" };

    const insuranceId: TableCellMultiline = {
      mainText: item?.insuranceid ?? "",
      secondaryText: `BIN : ${item?.bin ?? ""}`,
    };
    const cmm: TableCellWithBadge = getCmmStatuS(
      item.meta?.cmm_st_rpa_status ?? "",
    );
    const textNote: TableCellWithBadge = getTextNotesStatus(
      item.text_note_status ?? "",
    );
    const letterUpload: TableCellWithBadge = getDenialLetterUploadStatus(
      item.meta?.denial_letter_upload_status ?? "",
    );

    const sendToAppeal: TableCellWithBadge = getSendToAppealBadgeButtonValue({
      responseStatus: item.response_status ?? null,
      appealStatus: item.appeal_status ?? null,
    });

    const binValue = item?.bin;
    const bin: string =
      !binValue || binValue === "nan" || binValue === "NaN"
        ? ""
        : String(binValue);

    const action: TableCellThreeDotsButton = {
      threeDotsOptions: [{ id: "rerun_status", text: "Rerun Status" }],
      rowId: item.identifier,
    };

    const detailsPopup: TableCellDotsWithPopup = {
      items: PHARMA_STP_DETAILS_POPUP_CHECKBOX_ITEMS,
      triggerAriaLabel: `CMM, text note, and letter upload checklist for ${
        item.identifier ?? "order"
      }`,
      rowIdentifier: identifier,
      markSuccessBigQueryFieldByCheckboxId: PHARMA_STP_DETAILS_POPUP_BQ_FIELDS,
      onMarkSuccess: callbacks?.onStpDetailsPopupMarkSuccess,
    };

    const callingReasonText =
      typeof item.non_epa_reason === "string" ? item.non_epa_reason.trim() : "";

    const isCallingResolved =
      (item.non_epa_status ?? "").toLowerCase() ===
      CallingStatus.RESOLVED.toLowerCase();

    /** Same green as the former resolved-calling phone icon; drawn as full-row left border in `TableBody`. */
    const resolvedCallingStripColor = "#16a34a";
    const rowLeftAccentColor = isCallingResolved
      ? resolvedCallingStripColor
      : undefined;
    const rowLeftAccentTitle = isCallingResolved
      ? callingReasonText.trim() || undefined
      : undefined;

    const extendedFields = mapPharmaStpFileExtendedRowFields(item);

    const row = {
      batch,
      dow,
      patient_mrn,
      patientName,
      dob,
      drug,
      letterUpload,
      cmm,
      textNote,
      bin,
      insuranceId,
      cmmId,
      secondStpStatus: secondStpStatusBadge,
      responseStpStatus: responseStpStatusBadge,
      routeVia: routeViaBadge,
      add,
      poc,
      sftp,
      sendToAppeal,
      detailsPopup,
      identifier,
      [PharmaStpFileTableColumnKeys.INSURANCE_INFO_ICON]: identifier,
      rowLeftAccentColor,
      rowLeftAccentTitle,
      textNoteStatus: item.text_note_status,
      letterUploadStatus: item.meta?.denial_letter_upload_status,
      letterUploadUpdatedAt:
        item.meta?.denial_letter_upload_status_updated_at ?? "",
      cmmStatus: item.meta?.cmm_st_rpa_status,
      action,
      ...extendedFields,
    };

    return row;
  });
};
